"use client";

/**
 * AcceleratorsAnnouncementBanner: one-time card on the signed-in home page
 * telling a returning founder about Accelerators (apply to Techstars, YC and
 * more from the canvas). Presentational only: useAcceleratorsAnnouncementBanner
 * decides whether it is eligible to show and owns the href/dismiss wiring.
 *
 * Copy is fixed (operator-approved) and never implies a partnership with any
 * program — no co-brand lockup, no "in partnership with".
 */
import Link from "next/link";
import { X } from "lucide-react";

import { ACCELERATORS_ANNOUNCEMENT_COPY } from "@/lib/accelerators/announcementBanner";
import { FUNDRAISING_BTN_COMMIT } from "@/constants/canvasPanelDesign";
import { cn } from "@/lib/utils";

export interface AcceleratorsAnnouncementBannerProps {
  /** The canvas URL "Open Programs" opens — the hook's most-recent-project pick. */
  href: string;
  onButtonClick: () => void;
  onDismiss: () => void;
}

export function AcceleratorsAnnouncementBanner({
  href,
  onButtonClick,
  onDismiss,
}: AcceleratorsAnnouncementBannerProps) {
  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-neutral-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-semibold text-neutral-900">
          {ACCELERATORS_ANNOUNCEMENT_COPY.heading}
        </p>
        <p className="text-sm text-neutral-600">{ACCELERATORS_ANNOUNCEMENT_COPY.body}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Link href={href} className={cn(FUNDRAISING_BTN_COMMIT)} onClick={onButtonClick}>
          {ACCELERATORS_ANNOUNCEMENT_COPY.button}
        </Link>
        <button
          type="button"
          aria-label="Dismiss announcement"
          onClick={onDismiss}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
